import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { MobileOtpForm } from "@/components/MobileOtpForm";
import { GoogleButton } from "@/components/GoogleButton";

const tabCls = (active) =>
  `flex-1 h-10 rounded-md text-sm font-medium transition-colors ${
    active ? "bg-navy text-white" : "text-slate-500 hover:text-navy"
  }`;

export default function Login() {
  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();

  const go = (user) => navigate(user.role === "admin" ? "/admin" : "/dashboard");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerify(false);
    try {
      const user = await login(email, password);
      toast.success("Welcome back.");
      go(user);
    } catch (err) {
      if (err?.response?.status === 403) {
        setNeedsVerify(true);
      }
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await resendVerification(email);
      toast.success("Verification email sent. Please check your inbox.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-navy grain p-14">
        <Logo light />
        <div>
          <h2 className="font-display text-4xl font-bold text-white leading-tight">Learn with structure. Research with discipline.</h2>
          <p className="mt-4 text-slate-400">Access your courses, progress and research on the KRV Prime platform.</p>
        </div>
        <p className="text-xs text-slate-500">An independent financial-market education & research platform.</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display text-3xl font-bold text-navy">Login</h1>
          <p className="mt-2 text-sm text-slate-500">Welcome back to KRV Prime.</p>

          <div className="mt-6"><GoogleButton label="Continue with Google" /></div>
          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
          </div>

          <div className="flex gap-1 rounded-lg bg-slate-100 p-1" data-testid="login-mode-toggle">
            <button onClick={() => setMode("email")} className={tabCls(mode === "email")} data-testid="login-mode-email">Email</button>
            <button onClick={() => setMode("mobile")} className={tabCls(mode === "mobile")} data-testid="login-mode-mobile">Mobile OTP</button>
          </div>

          {mode === "email" ? (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="Email address" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" placeholder="Password" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
              <button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60">
                {loading ? "Signing in…" : "Login"}
              </button>
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-navy" data-testid="go-forgot-password">Forgot password?</Link>
              </div>
              {needsVerify && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="login-verify-notice">
                  Your email isn't verified yet.
                  <button type="button" onClick={resend} disabled={resending} className="ml-1 font-semibold underline hover:text-amber-900 disabled:opacity-60" data-testid="login-resend">
                    {resending ? "Sending…" : "Resend verification email"}
                  </button>
                </div>
              )}
            </form>
          ) : (
            <div className="mt-6"><MobileOtpForm onSuccess={go} /></div>
          )}

          <p className="mt-6 text-xs text-slate-400 text-center">
            By signing in, you agree to our <Link to="/terms" className="text-navy hover:text-gold underline">Terms & Conditions</Link> and <Link to="/privacy" className="text-navy hover:text-gold underline">Privacy Policy</Link>.
          </p>
          <p className="mt-4 text-sm text-slate-500 text-center">
            New to KRV Prime? <Link to="/register" className="text-navy font-medium hover:text-gold" data-testid="go-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
